// 🖥️ DesktopNav - Barre de navigation pour écrans larges
// Navigation complète avec logo, liens, CTA casting et radio
// Utilisé dans : src/components/layout/Navigation.jsx

import { useState, useEffect } from 'react'
import { Sun, Moon, Mic2 } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import globalData from '@/locales/global.json'
import { MiniRadioPlayer } from '@/components/RadioPlayer'

export default function DesktopNav({ darkMode, toggleDarkMode }) {
  // Hooks pour la gestion de l'état et de la localisation
  const location = useLocation()
  const { navigation, labels } = globalData

  /**
   * Vérifie si un chemin de navigation est actif
   * @param {string} path - Chemin à vérifier
   * @returns {boolean} true si le chemin correspond à la page actuelle
   */
  const isActive = (path) => location.pathname === path

  // Navigation publique uniquement (filtrée depuis les données globales)
  const visibleNavItems = navigation.desktop

  return (
    // Navigation fixe en haut avec effet glassmorphism
    <nav className="hidden md:block fixed top-0 left-0 right-0 bg-base-light/95 dark:bg-base-dark/95 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-800 z-50 transition-colors overflow-visible">
      <div className="h-16 flex items-center justify-between px-6 overflow-visible">
          {/* Logo Rock'In Agde avec effet hover */}
          <Link to="/" className="group">
            <img 
              src={darkMode ? "/images/logos/logo-vertical-white.png" : "/images/logos/logo-vertical.jpg"} 
              alt="Rock'In Agde" 
              className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Liens de navigation principaux */}
          <div className="flex gap-6">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-neutral-700 dark:text-neutral-300 hover:text-primary-500 dark:hover:text-primary-400'
                }`}
              >
                {item.label}
                {/* Indicateur de page active */}
                {isActive(item.path) && (
                  <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Actions à droite : Casting + Radio + Thème */}
          <div className="flex items-center gap-3 relative">
            {/* Bouton Casting CTA avec icône */}
            <Link
              to="/casting"
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
            >
              <Mic2 size={18} />
              <span className="text-sm">{labels.casting}</span>
            </Link>

            {/* Radio Player Mini avec contrôles au hover - Container avec espace pour le panel */}
            <div className="relative">
              <MiniRadioPlayer isDesktop={true} />
            </div>

            {/* Bouton toggle Dark Mode */}
            <button
              onClick={toggleDarkMode}
              className="p-2.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              aria-label={labels.darkMode}
            >
              {darkMode ? (
                <Sun className="text-neutral-600 dark:text-neutral-400" size={20} />
              ) : (
                <Moon className="text-neutral-600" size={20} />
              )}
            </button>
          </div>
      </div>
    </nav>
  )
}
